import { z } from 'zod';
import { AuthIdentitySummarySchema } from './auth.js';

// ─── GET /me ──────────────────────────────────────────────────────────────────

export const ProfileSchema = z.object({
  id: z.string(),
  telegramUserId: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
  identities: z.array(AuthIdentitySummarySchema),
});
export type Profile = z.infer<typeof ProfileSchema>;

export const UpdateProfileSchema = z.object({
  displayName: z.string().trim().min(1).max(120),
});
export type UpdateProfileDto = z.infer<typeof UpdateProfileSchema>;

export const ProfileAccessStateSchema = z.enum(['owned', 'recovery_needed']);
export type ProfileAccessState = z.infer<typeof ProfileAccessStateSchema>;

export const ProfileAccessSourceSchema = z.enum(['entitlement', 'purchase']);
export type ProfileAccessSource = z.infer<typeof ProfileAccessSourceSchema>;

export const ProfileAccessItemSchema = z.object({
  clubId: z.string(),
  clubTitle: z.string(),
  bookAuthor: z.string().nullable(),
  accessState: ProfileAccessStateSchema,
  source: ProfileAccessSourceSchema,
});
export type ProfileAccessItem = z.infer<typeof ProfileAccessItemSchema>;

export const ProfilePurchaseHistoryItemSchema = z.object({
  id: z.string(),
  batchSource: z.string(),
  sourceOrderId: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paidAt: z.string().nullable(),
  mappedClubId: z.string().nullable(),
  mappedClubTitle: z.string().nullable(),
});
export type ProfilePurchaseHistoryItem = z.infer<typeof ProfilePurchaseHistoryItemSchema>;

export const ProfileAccessOverviewSchema = z.object({
  linkedEmail: z.string().nullable(),
  accessItems: z.array(ProfileAccessItemSchema),
  transactions: z.array(ProfilePurchaseHistoryItemSchema),
  hasRecoveryIssue: z.boolean(),
});
export type ProfileAccessOverview = z.infer<typeof ProfileAccessOverviewSchema>;

export const ProfileRecoveryLinkEmailBodySchema = z.object({
  accessToken: z.string().trim().min(1),
});
export type ProfileRecoveryLinkEmailBodyDto = z.infer<typeof ProfileRecoveryLinkEmailBodySchema>;

export const ProfileRecoveryLinkEmailResultSchema = z.object({
  profile: ProfileSchema,
  access: ProfileAccessOverviewSchema,
  grantedClubIds: z.array(z.string()),
});
export type ProfileRecoveryLinkEmailResult = z.infer<typeof ProfileRecoveryLinkEmailResultSchema>;

// ─── GET /profile/stats ───────────────────────────────────────────────────────

export const ProfileStatsSchema = z.object({
  clubsStarted: z.number(),
  chaptersCompleted: z.number(),
  totalReadingTimeSeconds: z.number(),
});
export type ProfileStats = z.infer<typeof ProfileStatsSchema>;
